
import { useState, useEffect, createContext, useContext } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    let subscription: { unsubscribe: () => void } | null = null;

    const finishAuth = (sess: Session | null) => {
      if (!mounted) return;
      setSession(sess);
      setUser(sess?.user ?? null);
      setIsLoading(false);
    };

    const initializeAuth = async () => {
      try {
        // Set up auth state listener FIRST (fires on sign in/out)
        const { data: { subscription: sub } } = supabase.auth.onAuthStateChange(
          (event, sess) => {
            if (!mounted) return;
            if (event === 'SIGNED_OUT') {
              localStorage.clear();
              window.dispatchEvent(new CustomEvent('userSignedOut'));
            }
            setSession(sess);
            setUser(sess?.user ?? null);
            setIsLoading(false);
          }
        );
        subscription = sub;

        // Get initial session with timeout - don't hang if Supabase is slow
        const SESSION_TIMEOUT_MS = 10000;
        const sessionPromise = supabase.auth.getSession();
        const timeoutPromise = new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('getSession timeout')), SESSION_TIMEOUT_MS)
        );

        const { data: { session: sess }, error } = await Promise.race([
          sessionPromise,
          timeoutPromise,
        ]).catch((err) => {
          console.warn('Auth getSession failed or timed out:', err?.message || err);
          return { data: { session: null }, error: err };
        }) as { data: { session: Session | null }; error: Error | null };

        if (error) {
          console.error('Error getting session:', error);
        }
        finishAuth(sess ?? null);
      } catch (error) {
        console.error('Auth initialization error:', error);
        finishAuth(null);
      }
    };

    initializeAuth();

    // Failsafe: Force loading to false after 8s if getSession/onAuthStateChange never settled
    const timeoutId = setTimeout(() => {
      if (mounted) {
        setIsLoading((prev) => {
          if (prev) {
            console.warn('Auth loading timeout - forcing loading to false');
            return false;
          }
          return prev;
        });
      }
    }, 8000);

    return () => {
      mounted = false;
      clearTimeout(timeoutId);
      subscription?.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    try {
      // Check if we have a valid session first
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      
      if (currentSession) {
        const { error } = await supabase.auth.signOut();
        
        if (error) {
          console.warn('Sign out error (but continuing cleanup):', error);
        }
      }
      
      // Always clear local state and storage, regardless of Supabase response
      setSession(null);
      setUser(null);
      
      // Clear ALL localStorage data to prevent data leakage
      try {
        localStorage.clear();
      } catch (error) {
        console.warn('Error clearing localStorage:', error);
      }
      
      // Dispatch cleanup event
      try {
        window.dispatchEvent(new CustomEvent('userSignedOut'));
      } catch (error) {
        console.warn('Error dispatching signout event:', error);
      }
      
    } catch (error) {
      console.error('🔐 [useAuth] Sign out process error:', error);
      
      // Even if there's an error, clear local state
      setSession(null);
      setUser(null);
      localStorage.clear();
      window.dispatchEvent(new CustomEvent('userSignedOut'));
      
      // Re-throw error so the calling component can handle it
      throw error;
    }
  };

  const value = {
    user,
    session,
    isLoading,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
